/**
 * Presupuestos — lista, `TDS-CORE` Parte 2 §2, §4.
 *
 * Los contadores de cuota se ven SIEMPRE (`URS §54`, `§110`): el límite no se
 * descubre al chocar con él. El estado que se muestra es el que da la API, que
 * ya cuenta como caducado un enviado cuya validez pasó (§2.4).
 *
 * El buscador filtra la lista que ya llegó (la API la manda entera), por número
 * o cliente y sin tildes, como el de Compras (prueba del propietario en
 * staging, 2026-10-25). No cambia los contadores: son de la organización.
 *
 * `OD-0062` (ACCEPTED 2026-10-25): la ficha «Caducan pronto» (E-08, enviados
 * que caducan en 7 días; lo decide la API) y «Descargar Excel» con el filtro
 * elegido (E-06, lo genera la API, en todos los planes).
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import {
  api,
  CADUCAN_PRONTO,
  PENDIENTES_FACTURAR,
  type CuotasPresupuestos,
  type EstadoPresupuesto,
  type FacturacionPresupuesto,
  type ResumenPresupuesto,
} from "../api/cliente";
import { t } from "../i18n/es";
import { coincide } from "../ui/coincide";
import { diaLegible, importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { InsigniaEstado } from "../ui/Insignia";
import { Aviso, Boton, Campo, FilaPulsable, Tarjeta } from "../ui/primitivas";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | {
      readonly tipo: "datos";
      readonly quotes: readonly ResumenPresupuesto[];
      readonly cuotas: CuotasPresupuestos | null;
      readonly porEstado: Readonly<Record<EstadoPresupuesto, number>> | null;
      /** Por presupuesto, lo facturado. Sin la respuesta, la lista sale como antes. */
      readonly facturacion: ReadonlyMap<string, FacturacionPresupuesto>;
      /** Los aceptados sin factura emitida; null si la respuesta no llegó. */
      readonly pendientes: ReadonlySet<string> | null;
      /** E-08: los enviados que caducan en 7 días, según la API; null si no llegó. */
      readonly caducanPronto: number | null;
    };

/** El tipo da el color (`.facturacion-*`): lo hecho, lo que falta y lo anulado no se leen igual. */
type ParteFacturacion = { readonly tipo: "emitida" | "borrador" | "anulada" | "anticipo" | "anticipo-borrador"; readonly texto: string };

/** Lo que la fila dice de la facturación del presupuesto; vacío si no hay nada. */
function partesFacturacion(f: FacturacionPresupuesto | undefined): readonly ParteFacturacion[] {
  if (f === undefined) return [];
  const partes: ParteFacturacion[] = [];
  if (f.invoiceStatus === "ISSUED") partes.push({ tipo: "emitida", texto: t.presupuestos.facturado(f.invoiceNumber, f.advanceNumbers ?? []) });
  else if (f.invoiceStatus === "DRAFT") partes.push({ tipo: "borrador", texto: t.presupuestos.facturaEnBorrador });
  else if (f.invoiceStatus === "VOIDED") partes.push({ tipo: "anulada", texto: t.presupuestos.facturaAnulada(f.invoiceNumber) });
  // Con la final emitida, el presupuesto ya está facturado entero: el porcentaje sobra.
  if (f.percentAdvanced !== null && f.invoiceStatus !== "ISSUED") partes.push({ tipo: "anticipo", texto: t.presupuestos.anticipos(f.percentAdvanced) });
  if (f.advanceDraftPending) partes.push({ tipo: "anticipo-borrador", texto: t.presupuestos.anticipoEnBorrador });
  return partes;
}

const ESTADOS = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED", "CANCELLED"] as const;

/**
 * No es un estado del presupuesto sino un filtro sobre los aceptados: los que
 * aún no tienen factura emitida, con anticipos o sin ellos. Lo decide la API.
 */
const PENDIENTES = PENDIENTES_FACTURAR;
type Filtro = EstadoPresupuesto | "TODOS" | typeof PENDIENTES | typeof CADUCAN_PRONTO;

/** Entrega el fichero al navegador como una descarga normal, con su nombre. */
function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function PantallaPresupuestos({
  onAbrir,
  onNuevo,
  onSesionCaducada,
}: {
  onAbrir: (id: string) => void;
  onNuevo: () => void;
  onSesionCaducada: () => void;
}) {
  const [filtro, setFiltro] = useState<Filtro>("TODOS");
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [busqueda, setBusqueda] = useState("");
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState(false);

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const [lista, cuotas, resumen, facturacion] = await Promise.all([
      api.listarPresupuestos(filtro === "TODOS" ? null : filtro === PENDIENTES ? "ACCEPTED" : filtro),
      api.cuotasPresupuestos(),
      api.resumenPresupuestos(),
      api.facturacionPresupuestos(),
    ]);
    if (!lista.ok) {
      if (lista.estado === 401) onSesionCaducada();
      else setEstado({ tipo: lista.estado === 403 ? "sin-permiso" : "error" });
      return;
    }
    const pendientes = facturacion.ok ? new Set(facturacion.datos.pendingQuoteIds) : null;
    // Sin saber cuáles faltan, el filtro de pendientes no puede responder.
    if (filtro === PENDIENTES && pendientes === null) {
      if (!facturacion.ok && facturacion.estado === 401) onSesionCaducada();
      else setEstado({ tipo: "error" });
      return;
    }
    setEstado({
      tipo: "datos",
      quotes: filtro === PENDIENTES && pendientes !== null ? lista.datos.filter((q) => pendientes.has(q.id)) : lista.datos,
      cuotas: cuotas.ok ? cuotas.datos : null,
      porEstado: resumen.ok ? resumen.datos.byStatus : null,
      facturacion: new Map(facturacion.ok ? facturacion.datos.quotes.map((f) => [f.quoteId, f]) : []),
      pendientes,
      caducanPronto: resumen.ok ? resumen.datos.expiringSoon : null,
    });
  }, [filtro, onSesionCaducada]);

  // E-06: el filtro elegido, tal cual, y la API arma el fichero.
  async function descargar(): Promise<void> {
    setDescargando(true);
    setErrorDescarga(false);
    const r = await api.descargarPresupuestos(filtro === "TODOS" ? null : filtro);
    setDescargando(false);
    if (r.ok) return guardar(r.datos.contenido, r.datos.nombre);
    if (r.estado === 401) return onSesionCaducada();
    setErrorDescarga(true);
  }

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cuotas = estado.tipo === "datos" ? estado.cuotas : null;
  const visibles = estado.tipo === "datos" ? estado.quotes.filter((q) => coincide(busqueda, q.number, q.customerName)) : [];
  const sinResultados = estado.tipo === "datos" && estado.quotes.length > 0 && visibles.length === 0;
  const mes = cuotas?.quotesCreatedThisMonth ?? null;
  const abiertos = cuotas?.quotesActive ?? null;
  const lleno = (mes !== null && mes.available === 0) || (abiertos !== null && abiertos.available === 0);
  const aviso = !lleno && ((mes?.warningLevel ?? 0) >= 80 || (abiertos?.warningLevel ?? 0) >= 80);

  // Sólo los estados que tienen algo (y el elegido): nadie pulsa un filtro para
  // ver una lista vacía. Si el recuento no llega, se enseñan todos, sin número.
  const porEstado = estado.tipo === "datos" ? estado.porEstado : null;
  const total = porEstado === null ? null : ESTADOS.reduce((n, e) => n + (porEstado[e] ?? 0), 0);
  const pendientes = estado.tipo === "datos" ? estado.pendientes : null;
  const caducanPronto = estado.tipo === "datos" ? estado.caducanPronto : null;
  const hayCaducanPronto = caducanPronto !== null && caducanPronto > 0;
  const fichas: ReadonlyArray<{ readonly valor: Filtro; readonly cuenta: number | null }> = [
    { valor: "TODOS", cuenta: total },
    ...ESTADOS.filter((e) => porEstado === null || (porEstado[e] ?? 0) > 0 || e === filtro).map((e) => ({
      valor: e,
      cuenta: porEstado === null ? null : (porEstado[e] ?? 0),
    })),
    // Como los estados: sólo si hay alguno, o si es el elegido.
    ...((pendientes !== null && pendientes.size > 0) || filtro === PENDIENTES
      ? [{ valor: PENDIENTES, cuenta: pendientes === null ? null : pendientes.size }]
      : []),
    // E-08: como el resto, sólo si hay alguno o si es el elegido.
    ...(hayCaducanPronto || filtro === CADUCAN_PRONTO ? [{ valor: CADUCAN_PRONTO, cuenta: caducanPronto }] : []),
  ];

  /*
    Sin NINGUN presupuesto, la pantalla explica que hacer, como Productos y
    Facturas, en vez de una franja con un aviso y el resto en blanco
    (OD-0015 D-06; auditoria de diseno, 2026-10-19). Con la cuota llena el
    boton sigue deshabilitado, igual que el de arriba.
  */
  if (estado.tipo === "datos" && total === 0 && filtro === "TODOS") {
    return (
      <Tarjeta titulo={t.presupuestos.titulo}>
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="presupuestos" />
          </span>
          <p className="estado-vacio-titulo">{t.presupuestos.vacioTitulo}</p>
          <p>{t.presupuestos.vacio}</p>
          <Boton onClick={onNuevo} deshabilitado={lleno}>
            <Icono nombre="mas" />
            {t.presupuestos.crearPrimero}
          </Boton>
        </div>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={t.presupuestos.titulo}>
      {mes !== null && abiertos !== null ? (
        <div className="progreso">
          {mes.limit === null ? null : <progress value={mes.used} max={mes.limit} aria-labelledby="cuota-mes" />}
          <p id="cuota-mes" className="ayuda-neutra">
            {mes.limit === null
              ? t.presupuestos.cuotaMesSinLimite(mes.used)
              : t.presupuestos.cuotaMes(mes.used, mes.limit, mes.resetsOn)}
          </p>
          <p className="ayuda-neutra">
            {abiertos.limit === null
              ? t.presupuestos.cuotaAbiertosSinLimite(abiertos.used)
              : t.presupuestos.cuotaAbiertos(abiertos.used, abiertos.limit)}
          </p>
        </div>
      ) : null}
      {mes !== null && mes.available === 0 && mes.limit !== null ? (
        <Aviso tono="aviso">{t.presupuestos.cuotaLlenaMes(mes.limit, mes.resetsOn)}</Aviso>
      ) : null}
      {abiertos !== null && abiertos.available === 0 && abiertos.limit !== null ? (
        <Aviso tono="aviso">{t.presupuestos.cuotaLlenaAbiertos(abiertos.limit)}</Aviso>
      ) : null}
      {aviso ? <Aviso tono="info">{t.presupuestos.avisoCuota}</Aviso> : null}

      <div className="barra-acciones">
        <div className="fichas" role="group" aria-label={t.presupuestos.filtro}>
          {fichas.map((f) => (
            <button
              key={f.valor}
              type="button"
              className="ficha"
              aria-pressed={filtro === f.valor}
              onClick={() => setFiltro(f.valor)}
            >
              {f.valor === "TODOS" || f.valor === PENDIENTES || f.valor === CADUCAN_PRONTO ? null : (
                <span className={`punto punto-${f.valor}`} aria-hidden="true" />
              )}
              <span>
                {f.valor === "TODOS"
                  ? t.presupuestos.todos
                  : f.valor === PENDIENTES
                    ? t.presupuestos.pendientesDeFacturar
                    : f.valor === CADUCAN_PRONTO
                      ? t.presupuestos.caducanPronto
                      : t.presupuestos.estados[f.valor]}
              </span>
              {f.cuenta === null ? null : <span className="ficha-cuenta">{f.cuenta}</span>}
            </button>
          ))}
          {/* Todos los estados, también los que están a cero (petición del propietario). */}
          <select
            className="ficha-selector"
            aria-label={t.presupuestos.todosLosEstados}
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as Filtro)}
          >
            <option value="TODOS">{t.presupuestos.todosLosEstados}</option>
            {ESTADOS.map((e) => (
              <option key={e} value={e}>
                {porEstado === null ? t.presupuestos.estados[e] : `${t.presupuestos.estados[e]} (${porEstado[e] ?? 0})`}
              </option>
            ))}
            <option value={PENDIENTES}>
              {pendientes === null ? t.presupuestos.pendientesDeFacturar : `${t.presupuestos.pendientesDeFacturar} (${pendientes.size})`}
            </option>
            <option value={CADUCAN_PRONTO}>
              {caducanPronto === null ? t.presupuestos.caducanPronto : `${t.presupuestos.caducanPronto} (${caducanPronto})`}
            </option>
          </select>
        </div>
        <div className="fila-botones">
          {estado.tipo === "datos" && estado.quotes.length > 0 ? (
            <Boton variante="secundario" onClick={() => void descargar()} deshabilitado={descargando}>
              {descargando ? t.presupuestos.descargando : t.presupuestos.descargar}
            </Boton>
          ) : null}
          <Boton onClick={onNuevo} deshabilitado={lleno || estado.tipo !== "datos"}>
            {t.presupuestos.nuevo}
          </Boton>
        </div>
      </div>
      {errorDescarga ? <Aviso tono="error">{t.presupuestos.errorDescarga}</Aviso> : null}

      {estado.tipo === "cargando" ? <p>{t.presupuestos.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.presupuestos.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.presupuestos.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.presupuestos.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "datos" && estado.quotes.length === 0 ? (
        <Aviso tono="info">
          {filtro === "TODOS"
            ? t.presupuestos.vacio
            : filtro === PENDIENTES
              ? t.presupuestos.vacioPendientes
              : filtro === CADUCAN_PRONTO
                ? t.presupuestos.vacioCaducanPronto
                : t.presupuestos.vacioFiltro}
        </Aviso>
      ) : null}

      {estado.tipo === "datos" && estado.quotes.length > 0 ? (
        <Campo id="pre-buscar" etiqueta={t.presupuestos.buscar} valor={busqueda} onChange={setBusqueda} autoComplete="off" requerido={false} />
      ) : null}
      {sinResultados ? <Aviso tono="info">{t.presupuestos.sinResultados}</Aviso> : null}

      {visibles.length > 0 ? (
        <ul className="lista-filas">
          {visibles.map((q) => {
            const facturacion = partesFacturacion(estado.tipo === "datos" ? estado.facturacion.get(q.id) : undefined);
            return (
              <FilaPulsable key={q.id} onClick={() => onAbrir(q.id)}>
                <span className="fila-titulo">
                  {q.number} · {q.customerName ?? ""}
                </span>
                <span className="ayuda-neutra">
                  <InsigniaEstado estado={q.status} />
                  {q.invoiceTotal === null ? "" : ` · ${t.presupuestos.total(importeLegible(q.invoiceTotal))}`}
                  {facturacion.length === 0 ? null : " · "}
                  {facturacion.map((p, i) => (
                    <Fragment key={p.tipo}>
                      {i === 0 ? null : ", "}
                      <strong className={`facturacion facturacion-${p.tipo}`}>{p.texto}</strong>
                    </Fragment>
                  ))}
                  {" · "}
                  {q.validUntil === null ? t.presupuestos.sinValidez : t.presupuestos.validoHasta(diaLegible(q.validUntil))}
                </span>
              </FilaPulsable>
            );
          })}
        </ul>
      ) : null}
    </Tarjeta>
  );
}
